import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { verifyEmailThunk, resendVerificationCodeThunk } from "../../store/slices/authThunks";
import { logout } from "../../store/slices/authSlice";
import logo from "../../images/Nkenne Ai-01 2.png";
import loginIllustration from "../../images/Learning language with friend.png";

const VerifyEmail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useSelector((state) => state.auth);

  // Get email from location state or redirect to signup
  const email = location.state?.email;

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      toast.error("Please sign up first to verify your email.");
      navigate("/signup");
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);

    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);

    // Focus last filled input or next empty
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const verificationCode = code.join("");

    if (verificationCode.length !== 6) {
      toast.error("Please enter the complete 6-digit code.");
      return;
    }

    try {
      const result = await dispatch(verifyEmailThunk({ email, code: verificationCode }));

      if (verifyEmailThunk.fulfilled.match(result)) {
        console.log("Email verification successful:", result.payload);
        toast.success("Email verified successfully! Welcome to NKENNEAi!", {
          position: "top-right",
          autoClose: 3000,
        });

        // Small delay to ensure Redux state is updated
        setTimeout(() => {
          // Redirect based on role
          const redirectPath = result.payload.role === 'admin' ? '/admin-dashboard' : '/user-dashboard';
          console.log("Redirecting to:", redirectPath);
          // Show welcome modal and onboarding section after email verification
          if (redirectPath === '/user-dashboard') {
            navigate(redirectPath, { state: { showWelcomeModal: true, showOnboarding: true } });
          } else {
            navigate(redirectPath);
          }
        }, 1000);
      } else if (verifyEmailThunk.rejected.match(result)) {
        console.error("Email verification failed:", result.payload);
        toast.error(result.payload || "Verification failed. Please check your code.", {
          position: "top-right",
          autoClose: 4000,
        });
      }
    } catch (error) {
      console.error("Verification error:", error);
      toast.error("An unexpected error occurred. Please try again.", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;

    setResendLoading(true);
    try {
      const result = await dispatch(resendVerificationCodeThunk(email));

      if (resendVerificationCodeThunk.fulfilled.match(result)) {
        toast.success("Verification code sent! Please check your email.", {
          position: "top-right",
          autoClose: 3000,
        });
        setResendTimer(60); // 60 seconds cooldown
        setCode(["", "", "", "", "", ""]); // Clear code inputs
        inputRefs.current[0]?.focus();
      } else if (resendVerificationCodeThunk.rejected.match(result)) {
        toast.error(result.payload || "Failed to resend code. Please try again.", {
          position: "top-right",
          autoClose: 4000,
        });
      }
    } catch (error) {
      console.error("Resend error:", error);
      toast.error("An unexpected error occurred. Please try again.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleUpdateEmail = () => {
    dispatch(logout());
    navigate("/signup");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Header with Logo and Back Button */}
      <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-4 md:p-6">
        <Link to="/">
          <div className="flex items-center bg-white rounded-lg px-3 py-2 shadow-sm">
            <img src={logo} alt="NkenneAi Logo" className="w-full h-8 md:h-10" />
          </div>
        </Link>
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Website
        </button>
      </div>

      {/* Left Side - Illustration */}
      <div className="md:flex-1 bg-secondary flex items-center justify-center p-4 md:p-8 hidden md:flex">
        <img
          src={loginIllustration}
          alt="Illustration"
          className="max-w-[70%] h-auto"
        />
      </div>

      {/* Right Side - Verification Form */}
      <div className="flex-1 bg-white flex items-center justify-center p-4 md:p-8 pt-20 md:pt-24">
        <div className="w-full max-w-md space-y-6">
          {/* Logo and Branding */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <img src={logo} alt="Logo" className="w-32 md:w-50 md:h-50" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-8">
              Confirm Your Email Address
            </h1>
          </div>

          <div className="space-y-6 border p-6 rounded-xl">
            {/* Email Address Display */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                We sent a code to     <span className="text-sm text-gray-900 font-bold mt-1">
                  {email}
                </span>
              </p>

            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Code Input */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3 text-left">
                  Enter Code
                </label>
                <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-12 md:w-14 md:h-14 text-center text-xl font-bold border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
              </div>

              {/* Verify Button */}
              <button
                type="submit"
                disabled={loading || code.join("").length !== 6}
                className="w-full bg-secondary hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm"
              >
                {loading ? "Verifying..." : "Continue"}
              </button>
            </form>

            {/* Help Links */}
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">
                Didn't get your code?
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendLoading || resendTimer > 0}
                  className="block text-secondary hover:text-teal-500 font-bold text-sm disabled:text-gray-400 disabled:cursor-not-allowed transition-colors mx-auto underline"
                >
                  {resendLoading
                    ? "Sending..."
                    : resendTimer > 0
                      ? `Resend the code (${resendTimer}s)`
                      : "Resend the code"}
                </button>
                <span className="text-gray-600">or</span>
                <button
                  onClick={handleUpdateEmail}
                  className="block text-secondary hover:text-teal-500 font-bold text-sm mx-auto underline bg-transparent border-none cursor-pointer"
                >
                  update your email address
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;

